import React, { useEffect, useRef, useState } from 'react';

const CloudflareTurnstile = ({ sitekey, onVerify, onError, className, style, scriptSrc = 'https://challenges.cloudflare.com/turnstile/v0/api.js' }) => {
    const turnstileRef = useRef(null);
    const widgetId = useRef(null);
    const [scriptLoaded, setScriptLoaded] = useState(!!window.turnstile);
    const [scriptError, setScriptError] = useState(null);

    useEffect(() => {
        if (scriptLoaded) return; // Skip if script is already loaded

        const script = document.createElement('script');
        script.src = scriptSrc;
        script.async = true;
        script.onload = () => {
            setScriptLoaded(true);
        };
        script.onerror = () => {
            setScriptError('Failed to load Turnstile script');
            onError('Failed to load Turnstile script');
        };
        document.head.appendChild(script);

        return () => {
            if (script.parentNode) {
                document.head.removeChild(script);
            }
        };
    }, [scriptSrc]); // Removed scriptLoaded dependency to prevent re-running

    const renderTurnstile = () => {
        if (!window.turnstile || !turnstileRef.current || widgetId.current) return; // Prevent re-rendering if widget exists

        try {
            widgetId.current = window.turnstile.render(turnstileRef.current, {
                sitekey,
                callback: (token) => onVerify?.(token),
                'error-callback': (error) => onError?.(error),
                'expired-callback': () => onVerify?.(null),
                theme: 'light', // or 'dark', or 'auto'
            });
        } catch (error) {
            setScriptError('Failed to render Turnstile widget');
            onError(error.message);
        }
    };

    useEffect(() => {
        if (scriptLoaded) {
            renderTurnstile();
        }

        return () => {
            if (widgetId.current && window.turnstile) {
                try {
                    window.turnstile.remove(widgetId.current);
                    widgetId.current = null; // Reset widgetId to allow re-rendering
                } catch (error) {
                    console.warn('Failed to remove Turnstile widget:', error);
                }
            }
        };
    }, [sitekey, scriptLoaded]); // Removed onVerify, onError to avoid unnecessary re-renders

    if (scriptError) {
        return (
            <div className={className} style={{ backgroundColor: '#fff', color: '#000', padding: '10px', ...style }}>
                Error: {scriptError}
            </div>
        );
    }

    return (
        <div
            ref={turnstileRef}
            className={className}
            style={{ backgroundColor: '#fff', display: "flex", ...style }} // Light background for container
        />
    );
};

export default CloudflareTurnstile;